/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { privateConfig, publicConfig } from 'config'
import Requester, { RequestBody } from 'data-source/Requester'
import { codecDiscoveryProducts } from 'data-source/mappers/codecDiscoveryProducts'
import { codecGetAdvancedPlacement } from 'data-source/mappers/codecGetAdvancedPlacement'
import { codecGetAuthorNewPage } from 'data-source/mappers/codecGetAuthorNewPage'
import { codecGetAwardsPage } from 'data-source/mappers/codecGetAwardsPage'
import { codecGetCommonData } from 'data-source/mappers/codecGetCommonData'
import { codecGetDealBar } from 'data-source/mappers/codecGetDealBar'
import { codecGetDealsPage } from 'data-source/mappers/codecGetDealsPage'
import { codecGetGiveawaysPage } from 'data-source/mappers/codecGetGiveawaysPage'
import { codecGetHomePage } from 'data-source/mappers/codecGetHomePage'
import { codecGetMissedProps } from 'data-source/mappers/codecGetMissedProps'
import { codecGetNewsletterPage } from 'data-source/mappers/codecGetNewsletterPage'
import { codecGetNewslettersCatalog } from 'data-source/mappers/codecGetNewslettersCatalog'
import { codecGetPage404 } from 'data-source/mappers/codecGetPage404'
import { codecGetTagPage } from 'data-source/mappers/codecGetTagPage'
import { codecPostsList } from 'data-source/mappers/codecPostsList'
import { codecRedirect } from 'data-source/mappers/codecRedirect'
import { reviewProductScoreMapper } from 'data-source/mappers/reviewProductScoreMapper'
import { ApiDiscoveryProducts } from 'data-source/types.wp'
import { codecGetTplPage, componentItemMapper, contentBlockMapper } from 'ds/mappers'
import * as buildersOpts from 'ds/mappers/link-wrappers'
import { authorLinkBuilder, postLinkBuilder } from 'ds/mappers/link-wrappers'
import { linkBuilder as lb } from 'links'
import type { AlgoliaResponseProps } from 'shared-code/algolia-results'
import { cacher } from 'shared-code/cache'
import { logWarning } from 'shared-code/log'
import { isServerSide } from 'shared-code/util'
import { DSInitOptions, DSResponseWrapper, DSSubmitResponse } from 'shared-definitions/types'
import { DSFacade } from 'types.sg.ds'

function getBaseUrl(): string {
  const { baseUrl } = publicConfig('wpApiConfig')
  if (!baseUrl) {
    throw new Error('Undefined wp api baseUrl')
  }
  return baseUrl
}

export default function wp(options: DSInitOptions): DSFacade {
  const requester = new Requester({
    ...options,
    linkBuilder: (endpoint, query) => lb.linkBuilder(getBaseUrl() + endpoint, {}, query),
    csrfTokenGetter: async (): Promise<string> => {
      if (isServerSide()) {
        // server request
        const serverCsrfToken = privateConfig('serverCsrfToken')
        if (serverCsrfToken) {
          return serverCsrfToken
        }
        throw new Error('Undefined SERVER_CSRF_TOKEN')
      }

      // client
      const { data } = await getCsrfToken()
      return data
    },
  })

  /// ////////////////////////////////////////////////////////
  async function getCsrfToken(): Promise<DSResponseWrapper<string>> {
    const response = await requester.makeRequest('wpnonce')
    return { data: response.data }
  }

  /// ////////////////////////////////////////////////////////
  const sendFeedback: DSFacade['sendFeedback'] = feed =>
    requester.makeSubmitJson('feedback', {
      post_id: feed.postId,
      feedback: feed.feedback,
      is_negative: feed.isNegative,
    }) as ReturnType<DSFacade['sendFeedback']>

  /// ////////////////////////////////////////////////////////
  const sendContacts: DSFacade['sendContacts'] = feed =>
    requester.makeSubmitJson('forms/contact', feed as RequestBody) as ReturnType<
      DSFacade['sendFeedback']
    >

  /// ////////////////////////////////////////////////////////
  const sendSubscribe: DSFacade['sendSubscribe'] = async feed => {
    if (!isServerSide()) {
      // it can be done only on server side because service auth token is not accessible in browser
      const response = await fetch(lb.buildApiLink({ path: 'apiProxy' }), {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'sendSubscribe',
          feed,
        }),
      })
      return response.json() as Promise<DSSubmitResponse>
    }

    const apiKey = privateConfig('sailThruKey'),
      apiSecret = privateConfig('sailThruSecret')

    if (!apiKey || !apiSecret) {
      const err = 'Undefined SailThru credentials'
      logWarning(err)
      return { code: 500, message: err }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const SailThru = await import('shared-code/sailthru').then(m => m.default)
    const client = new SailThru(apiKey, apiSecret)

    try {
      const result = await client.subscribe(feed.email, feed.choices, feed.cookieId)
      return result
        ? { code: 200, message: 'Ok' }
        : { code: 500, message: 'Unknownd sailthru error' }
    } catch (e) {
      const err = 'SailTru failure'
      logWarning(err, e)
      return { code: 500, message: err }
    }
  }

  /// ////////////////////////////////////////////////////////
  const sendScore: DSFacade['sendScore'] = async feed => {
    const result = await requester.makeSubmitJson('user_rating', {
      post_id: feed.postId,
      key: feed.key,
      value: feed.val,
    })
    return reviewProductScoreMapper(result.data)
  }

  /// ////////////////////////////////////////////////////////
  const sendFaq: DSFacade['sendFaq'] = async feed =>
    (await requester.makeSubmitJson('forms/faq', {
      question: feed.question,
      ...(feed.postId ? { post_id: feed.postId } : {}),
    })) as ReturnType<DSFacade['sendFaq']>

  /// ////////////////////////////////////////////////////////
  const sendPoll: DSFacade['sendPoll'] = feed =>
    requester.makeSubmitJson('poll', {
      choice_id: feed.choiceId,
      poll_id: feed.pollId,
    }) as ReturnType<DSFacade['sendPoll']>

  /// ////////////////////////////////////////////////////////
  const getDealBar: DSFacade['getDealBar'] = async (id, query) => {
    const ipPart: { ip?: string } = query.ip ? { ip: query.ip } : {}
    const response = await requester.makeRequest(`dealbar/${id}`, {
      ...ipPart,
      type: query.type,
      ...(query.postId ? { post_id: query.postId } : {}),
    })
    return { data: codecGetDealBar(response, { postLinkBuilder }) }
  }

  /// ////////////////////////////////////////////////////////
  const getAds: DSFacade['getAds'] = async (slug, query) => {
    const ipPart: { ip?: string } = query.ip ? { ip: query.ip } : {}
    const response = await requester.makeRequest(`advanced_placement/${slug}`, {
      ...{
        ...ipPart,
        type: query.type,
      },
      ...(query.postId ? { post_id: query.postId ?? '' } : {}),
    })
    return { data: codecGetAdvancedPlacement(response, { componentItemMapper, postLinkBuilder }) }
  }

  /// ////////////////////////////////////////////////////////
  const getPostsList: DSFacade['getPostsList'] = async (taxonomy, page, per_page) => {
    const params = {
      page,
      per_page,
      'taxonomy[name]': taxonomy.name,
      'taxonomy[value]': taxonomy.value,
    }
    const response = await requester.makeRequest('post_list', params)
    return { data: codecPostsList(response, buildersOpts) }
  }

  const getTaxonomiedPostsList: DSFacade['getTaxonomiedPostsList'] = async (
    page,
    per_page,
    taxonomies
  ) => {
    const params = {
      page,
      per_page,
      ...taxonomies.reduce((carry, i) => {
        carry[`tax[${i.name}]`] = i.value
        return carry
      }, {} as Record<string, readonly number[]>),
    }
    const response = await requester.makeRequest('tax_posts', params)
    return { data: codecPostsList(response, buildersOpts) }
  }

  /// ////////////////////////////////////////////////////////
  const getNewslettersCatalog: DSFacade['getNewslettersCatalog'] = async () => {
    const response = await requester.makeRequest('newsletters')
    return {
      data: codecGetNewslettersCatalog(response, {
        pagePath: lb.buildSiteLink({ path: 'newsletters' }).href,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getNewsletterPage: DSFacade['getNewsletterPage'] = async slug => {
    const response = await requester.makeRequest(`newsletters/${slug}`)
    return {
      data: codecGetNewsletterPage(response, {
        pagePath: lb.buildSiteLink({ path: 'newsletterPage', params: { slug } }).href,
        ampPagePath: null,
        componentItemMapper,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getTplPage: DSFacade['getTplPage'] = async (slug, page, per_page) => {
    const response = await requester.makeRequest(
      `pages/${slug}`,
      page && per_page
        ? {
            page,
            per_page,
          }
        : {}
    )
    return { data: codecGetTplPage(response, slug) }
  }

  // maxItems:
  // 11 is 3 + 8 (typical number of recommendations on the page), it is used to
  // get 11 at one request and use it cached in session value
  const getRecommend: DSFacade['getRecommend'] = async (postId, from, to, maxItems) => {
    const maxRecommendations = Math.max(to, maxItems)
    const { credentials, getRelatedProducts } = await import('shared-code/algolia')
    const opts = credentials({
      appId: publicConfig('algoliaAppId'),
      apiKey: publicConfig('algoliaKeyId'),
      searchIndex: publicConfig('algoliaSearchIndex'),
    })
    if (!opts) {
      logWarning('Undefined algolia settings')
      return { data: { resource: 'recommendations', recommendations: null } }
    }

    const { convertItem } = await import('shared-code/algolia-results')
    const items = await getRelatedProducts(postId, maxRecommendations, {
      ...opts,
      convertItem: input => ({ ...convertItem(input, { postLinkBuilder, authorLinkBuilder }) }),
    })

    if (!items) {
      return { data: { resource: 'recommendations', recommendations: null } }
    }

    return {
      data: {
        resource: 'recommendations',
        recommendations: {
          items: items.slice(from, to),
          searchIndex: opts.searchIndex,
        },
      },
    }
  }

  /// ////////////////////////////////////////////////////////
  const getTagPage: DSFacade['getTagPage'] = async (slug, page, per_page) => {
    const response = await requester.makeRequest(
      `tag/${slug}`,
      page && per_page
        ? {
            page,
            per_page,
          }
        : {}
    )
    return {
      data: codecGetTagPage(response, {
        slug,
        pagePath: lb.buildSiteLink({ path: 'tag', params: { slug } }).href,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getDealsPage: DSFacade['getDealsPage'] = async (page, per_page) => {
    const response = await requester.makeRequest('deals', {
      page,
      per_page,
    })
    return {
      data: codecGetDealsPage(response, {
        pagePath: lb.buildSiteLink({ path: 'deals' }).href,
        ...buildersOpts,
      }),
    }
  }

  const getProductsSuggestions: DSFacade['getProductsSuggestions'] = async (
    query,
    page,
    per_page
  ) => {
    const { credentials } = await import('shared-code/algolia')
    const opts = credentials({
      appId: publicConfig('algoliaAppId'),
      apiKey: publicConfig('algoliaKeyId'),
      searchIndex: publicConfig('algoliaProductsSuggestionsIndex'),
    })
    if (!opts) {
      logWarning('Undefined algolia settings')
      return {
        data: {
          resource: 'products-suggestions',
          query,
          items: null,
        },
      }
    }

    const algoliasearch = await import('algoliasearch').then(m => m.default)
    const index = algoliasearch(opts.appId, opts.apiKey).initIndex(opts.searchIndex)

    const result = (await index.search(query, {
      clickAnalytics: true,
      page: page - 1, // algolia using 0 based, but we use 1 based pagination for entire site
      hitsPerPage: per_page,
      attributesToRetrieve: ['product_title'],
      responseFields: ['hits', 'page', 'hitsPerPage', 'nbHits'],
      attributesToHighlight: [],
    })) as unknown as AlgoliaResponseProps<{ product_title: string }>

    return {
      data: {
        resource: 'products-suggestions',
        query,
        items: {
          resource: 'paginated-list',
          perPage: result.hitsPerPage,
          items: result.hits.map(i => i.product_title),
          currentPage: result.page + 1,
          totalItems: result.nbHits,
        },
      },
    }
  }

  /// ////////////////////////////////////////////////////////
  const getSearchPage: DSFacade['getSearchPage'] = async (query, page, per_page) => {
    const { credentials, getSearchItems } = await import('shared-code/algolia')
    const opts = credentials({
      appId: publicConfig('algoliaAppId'),
      apiKey: publicConfig('algoliaKeyId'),
      searchIndex: publicConfig('algoliaSearchIndex'),
    })
    if (!opts) {
      logWarning('Undefined algolia settings')
      return {
        data: {
          resource: 'search-page',
          query,
          posts: null,
        },
      }
    }

    const { convertPaginated, attributesToRetrieve } = await import('shared-code/algolia-results')
    const posts = await getSearchItems(query, page, per_page, {
      ...opts,
      attributesToRetrieve,
      convertPaginated: input => ({
        ...convertPaginated(input, { postLinkBuilder, authorLinkBuilder }),
        searchIndex: opts.searchIndex,
        queryId: input.queryID ?? '',
      }),
    })

    return {
      data: {
        resource: 'search-page',
        query,
        posts,
      },
    }
  }

  /// ////////////////////////////////////////////////////////
  const getRedirect: DSFacade['getRedirect'] = async path => {
    const response = await requester.makeRequest(path.map(encodeURI).join('/'))
    return { data: codecRedirect(response) }
  }

  /// ////////////////////////////////////////////////////////
  const getGiveawaysPage: DSFacade['getGiveawaysPage'] = async (page, per_page) => {
    const response = await requester.makeRequest('pages/giveaways', {
      page,
      per_page,
    })
    return {
      data: codecGetGiveawaysPage(response, {
        pagePath: lb.buildSiteLink({ path: 'post', params: { slug: 'giveaways' } }).href,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getPage404: DSFacade['getPage404'] = async (hot, simple) => {
    const response = await requester.makeRequest('pages/404-2', {
      ...(hot
        ? {
            hot_page: hot.page,
            hot_per_page: hot.perPage,
          }
        : {}),
      ...(simple
        ? {
            page: simple.page,
            per_page: simple.perPage,
          }
        : {}),
    })
    return {
      data: codecGetPage404(response, {
        pagePath: lb.buildSiteLink({ path: 'post', params: { slug: 'notfound' } }).href,
        homeLink: lb.buildSiteLink({ path: 'home' }),
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getAwardsPage: DSFacade['getAwardsPage'] = async (page, per_page) => {
    const response = await requester.makeRequest('pages/awards', {
      page,
      per_page,
    })
    return {
      data: codecGetAwardsPage(response, {
        pagePath: lb.buildSiteLink({ path: 'post', params: { slug: 'awards' } }).href,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getDiscoveryAutocomplete: DSFacade['getDiscoveryAutocomplete'] = async (query, signal) => {
    const urlStr = publicConfig('discoveryAutocompleteApi')
    if (!urlStr) {
      throw new Error('Undefined discovery autocomplete api url')
    }
    const url = new URL(urlStr)
    url.searchParams.set('search', query)

    const response = await fetch(url.toString(), { signal })
    const json = await response.json()

    return { data: json as string[] }
  }

  /// ////////////////////////////////////////////////////////
  const getAuthorPage: DSFacade['getAuthorPage'] = async (
    slug: string,
    page: number,
    per_page: number
  ) => {
    const response = await requester.makeRequest('pages/author', {
      author_slug: slug,
      latest_posts_page: page,
      latest_posts_per_page: per_page,
    })
    return {
      data: codecGetAuthorNewPage(response, {
        slug,
        pagePath: lb.buildSiteLink({ path: 'author', params: { slug } }).href,
        ...buildersOpts,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  const getCommonData: DSFacade['getCommonData'] = async () =>
    cacher(
      'wp-common-data-response',
      async () => {
        const response = await requester.makeRequest('data')
        return {
          data: codecGetCommonData(response, {
            postLinkBuilder,
            homeLink: lb.buildSiteLink({ path: 'home' }),
            dealsLink: lb.buildSiteLink({ path: 'deals' }),
            searchLink: lb.buildSiteLink({ path: 'search' }),
            productsLink: lb.buildSiteLink({ path: 'products' }),
            profileLink: lb.buildSiteLink({ path: 'profile' }),
          }),
        }
      },
      60
    )

  /// ////////////////////////////////////////////////////////
  const getHomePage: DSFacade['getHomePage'] = async () => {
    const response = await requester.makeRequest('pages/home')
    return {
      data: codecGetHomePage(response, {
        pagePath: lb.buildSiteLink({ path: 'home' }).href,
        metaTitle: 'SoundGuys - We Live Audio!',
        metaDescription: 'We Live Audio!',
        contentBlockMapper,
      }),
    }
  }

  /// ////////////////////////////////////////////////////////
  /**
export interface DSDiscoveryFiltersState {
  category: string[]
  priceRange: DSRangeValue | null
  additional: Record<string, string[]>
  searchTerms: string[]
  ratings: DSVar<DSRangeValue>[]
  ratingFeatures: string[]
  sort: { order: DSSortOrder; field: string }
}
   */
  const getDiscoveryResults: DSFacade['getDiscoveryResults'] = async (filter, signal) => {
    const url = publicConfig('discoveryProductsApi')
    if (!url) {
      throw new Error('Undefined discovery products api url')
    }

    const params = new URLSearchParams()
    params.set('requested_from', publicConfig('clearbyRequestedFrom') ?? '')
    params.set('page', filter.page.toString())
    params.set('items_per_page', filter.perPage.toString())

    //  Adding category if it exists (multiply?)
    filter.category.forEach(category => {
      params.append('category[]', category)
    })

    // Adding brand if it exists (multiply?)
    filter.brand.forEach(brand => {
      params.append('brands[]', brand)
    })

    // add price range
    if (filter.priceRange) {
      params.set('try_min_price', filter.priceRange.min.toString())
      params.set('try_max_price', filter.priceRange.max.toString())
    }

    // add additional filters
    Object.entries(filter.additional).forEach(([key, values]) => {
      values.forEach(value => {
        params.append(`${key}[]`, value)
      })
    })

    // add search terms
    filter.searchTerms.forEach(term => {
      params.append('search[]', term)
    })

    // add ratings
    filter.ratings.forEach(rating => {
      params.append(`${rating.name}_min`, rating.value.min.toString())
      params.append(`${rating.name}_max`, rating.value.max.toString())
    })

    // add rating features
    filter.ratingFeatures.forEach(feature => {
      params.append('ratings[]', feature)
    })

    // add sort
    params.set('sort', filter.sort.field)
    params.set('sort_order', filter.sort.order)

    const results = await fetch(url + '?' + params.toString(), { signal })
    const json = (await results.json()) as ApiDiscoveryProducts

    return {
      data: {
        cards: codecDiscoveryProducts(json, buildersOpts),
      },
    }
  }

  /// ////////////////////////////////////////////////////////
  const getHomeMissed: DSFacade['getHomeMissed'] = async (page: number) => {
    const response = await requester.makeRequest('missed', { page })
    return { data: codecGetMissedProps(response.data.content, buildersOpts) }
  }

  return {
    getDiscoveryAutocomplete,
    getDiscoveryResults,
    sendFeedback,
    sendContacts,
    sendSubscribe,
    sendScore,
    sendPoll,
    sendFaq,
    getDealBar,
    getAds,
    getCsrfToken,
    getPostsList,
    getTaxonomiedPostsList,
    getNewslettersCatalog,
    getNewsletterPage,
    getTplPage,
    getTagPage,
    getDealsPage,
    getSearchPage,
    getProductsSuggestions,
    getRedirect,
    getGiveawaysPage,
    getPage404,
    getAwardsPage,
    getRecommend,
    getAuthorPage,
    getCommonData,
    getHomePage,
    getHomeMissed,
  }
}
